import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

const colorMap = {
  features: '#3366FF',
  improvements: '#AE0EB9',
  bug_fixes: '#DA4167',
};

export default function ReleaseLogItem({
  version,
  date,
  title,
  changes,
  totalChanges,
  expanded = false,
  onToggle = () => {},
}) {
  const [open, setOpen] = useState(expanded);
  useEffect(() => {
    setOpen(expanded);
  }, [expanded]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    onToggle(next);
  };
  const sections = [
    { key: 'features', label: 'New Features', items: changes?.features || [] },
    { key: 'improvements', label: 'Improvements', items: changes?.improvements || [] },
    { key: 'bug_fixes', label: 'Bug Fixes', items: changes?.bug_fixes || [] },
  ].filter((s) => s.items.length > 0);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        mb: 3,
        p: 3,
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
            <Typography variant="h6" fontWeight={600}>
              {version}
            </Typography>
            <Typography fontSize="0.85rem" color="text.secondary">
              {new Date(date).toLocaleDateString()}
            </Typography>
          </Box>
          <Typography fontWeight={500} color="text.primary" sx={{ mb: 1 }}>
            {title}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ mr: 1 }}>
            {totalChanges} changes
          </Typography>
          <IconButton
            size="small"
            onClick={toggle}
            disabled={sections.length === 0}
            sx={{ '&:focus': { outline: 'none' } }}
          >
            {open ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>

      {/* Sections */}
      {sections.length > 0 && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Box
            sx={{
              position: 'relative',
              pl: 2,
              mt: 2,
              '&:before': {
                content: '""',
                position: 'absolute',
                left: 14,
                top: 0,
                bottom: 0,
                width: 2,
                bgcolor: 'divider',
              },
            }}
          >
            {sections.map((sec, i) => (
              <Box
                key={sec.key}
                sx={{
                  display: 'flex',
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: colorMap[sec.key],
                    mt: '6px',
                    zIndex: 1,
                  }}
                />
                <Box
                  sx={{
                    ml: 2,
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(colorMap[sec.key], 0.07),
                    flexGrow: 1,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    sx={{ color: colorMap[sec.key], mb: 1 }}
                  >
                    {sec.label}
                  </Typography>
                  {sec.items.map((text, idx) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      sx={{ mt: idx > 0 ? 1 : 0, lineHeight: 1.6 }}
                    >
                      {text}
                    </Typography>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
